import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { TouchPad } from '../../components/TouchPad';
import { arcadeAudio } from '../../platform/audio';
import { Input } from '../../platform/input';
import type { GameProps } from '../../platform/types';
import './tidehaul.css';

const JOBS = [
  { from: [0, 0], to: [4, 3], name: 'Soup for 4B', time: 55 },
  { from: [4, 3], to: [-3, 4], name: 'Vinyl crate', time: 50 },
  { from: [-3, 4], to: [2, -4], name: 'Cake tower', time: 60 },
  { from: [2, -4], to: [-4, -2], name: 'Fish icebox', time: 50 },
  { from: [-4, -2], to: [5, 1], name: 'Night flowers', time: 55 },
  { from: [5, 1], to: [-1, 5], name: 'Radio parts', time: 50 },
  { from: [-1, 5], to: [3, -5], name: 'Legal papers', time: 45 },
  { from: [3, -5], to: [0, 0], name: 'The rush order', time: 40 },
];

export function TidehaulGame({ onExit, onResult }: GameProps) {
  const host = useRef<HTMLDivElement>(null);
  const input = useRef(new Input());
  const [hud, setHud] = useState({ job: 0, time: 55, cargo: 100, score: 0, name: JOBS[0].name });
  const [over, setOver] = useState<{ score: number; won: boolean } | null>(null);

  useEffect(() => {
    const el = host.current!;
    const inp = input.current.attach(el);
    void arcadeAudio.playMusic('tide', '/assets/music/Time-Driving.ogg');

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#87c6e8');
    scene.fog = new THREE.Fog('#87c6e8', 28, 70);
    const camera = new THREE.PerspectiveCamera(55, 16 / 9, 0.1, 120);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.setSize(el.clientWidth, Math.max(320, el.clientHeight));
    renderer.shadowMap.enabled = true;
    el.appendChild(renderer.domElement);

    const hemi = new THREE.HemisphereLight('#fff4d6', '#3a6b3a', 1.1);
    scene.add(hemi);
    const sun = new THREE.DirectionalLight('#fff1c8', 1.4);
    sun.position.set(20, 30, 10);
    sun.castShadow = true;
    scene.add(sun);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 80),
      new THREE.MeshStandardMaterial({ color: '#6bb36b' }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    const roadMat = new THREE.MeshStandardMaterial({ color: '#4a4f57' });
    for (let i = -5; i <= 5; i++) {
      const h = new THREE.Mesh(new THREE.BoxGeometry(80, 0.04, 2.2), roadMat);
      h.position.set(0, 0.02, i * 7);
      scene.add(h);
      const v = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.04, 80), roadMat);
      v.position.set(i * 7, 0.02, 0);
      scene.add(v);
    }

    const loader = new GLTFLoader();
    const car = new THREE.Group();
    const fallback = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.7, 2.4),
      new THREE.MeshStandardMaterial({ color: '#ffb020' }),
    );
    fallback.position.y = 0.4;
    fallback.castShadow = true;
    car.add(fallback);
    car.position.set(0, 0, 0);
    scene.add(car);

    const buildings: THREE.Object3D[] = [];
    const loadGltf = (url: string) =>
      new Promise<THREE.Group>((resolve, reject) => {
        loader.load(url, (g) => resolve(g.scene), undefined, reject);
      });

    void (async () => {
      try {
        const van = await loadGltf('/assets/glb/cars/delivery.glb');
        van.scale.set(1.1, 1.1, 1.1);
        van.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) {
            m.castShadow = true;
            m.receiveShadow = true;
            const mat = m.material as THREE.MeshStandardMaterial;
            if (mat && !mat.map) { mat.map = carTex; mat.needsUpdate = true; }
          }
        });
        car.clear();
        car.add(van);
      } catch { /* keep box */ }
      const names = 'abcdefgh'.split('');
      for (let x = -4; x <= 4; x++) {
        for (let z = -4; z <= 4; z++) {
          if (x === 0 || z === 0) continue;
          if ((x + z) % 2 === 0) continue;
          try {
            const letter = names[Math.abs(x + z) % names.length];
            const b = await loadGltf(`/assets/glb/city/building-type-${letter}.glb`);
            b.position.set(x * 7 + 2.4, 0, z * 7 + 2.4);
            b.scale.set(1.4, 1.4, 1.4);
            b.traverse((o) => {
              const m = o as THREE.Mesh;
              if (m.isMesh) {
                m.castShadow = true;
                m.receiveShadow = true;
                const mat = m.material as THREE.MeshStandardMaterial;
                if (mat && !mat.map) { mat.map = cityTex; mat.needsUpdate = true; }
              }
            });
            scene.add(b);
            buildings.push(b);
          } catch {
            const box = new THREE.Mesh(
              new THREE.BoxGeometry(2.2, 2 + Math.random() * 2, 2.2),
              new THREE.MeshStandardMaterial({ color: new THREE.Color().setHSL(0.08 + Math.random() * 0.1, 0.4, 0.5) }),
            );
            box.position.set(x * 7 + 2.4, 1.2, z * 7 + 2.4);
            box.castShadow = true;
            scene.add(box);
            buildings.push(box);
          }
        }
      }
    })();

    const texLoader = new THREE.TextureLoader();
    const cityTex = texLoader.load('/assets/glb/city/colormap.png');
    cityTex.colorSpace = THREE.SRGBColorSpace;
    const carTex = texLoader.load('/assets/glb/cars/colormap.png');
    carTex.colorSpace = THREE.SRGBColorSpace;

    const marker = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.7, 2.2, 12),
      new THREE.MeshStandardMaterial({ color: '#ff5d8f', emissive: '#ff5d8f', emissiveIntensity: 0.85 }),
    );
    marker.position.y = 1.2;
    scene.add(marker);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.1, 1.4, 24),
      new THREE.MeshBasicMaterial({ color: '#ff5d8f', side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.06;
    scene.add(ring);
    const traffic: Array<{ mesh: THREE.Group; x: number; z: number; dir: number; spd: number }> = [];
    for (let i = 0; i < 8; i++) {
      const m = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(1.1, 0.5, 2),
        new THREE.MeshStandardMaterial({ color: ['#e85d4c', '#4c8fe8', '#f2f2f2', '#222'][i % 4] }),
      );
      body.position.y = 0.35;
      m.add(body);
      const lane = (i % 2 === 0);
      m.position.set(lane ? (i - 4) * 7 : -28, 0, lane ? -28 : (i - 4) * 7);
      scene.add(m);
      traffic.push({ mesh: m, x: m.position.x, z: m.position.z, dir: lane ? 0 : 1, spd: 6 + Math.random() * 4 });
    }

    const vel = { x: 0, z: 0 };
    let yaw = 0;
    let job = 0;
    let time = JOBS[0].time;
    let cargo = 100;
    let score = 0;
    let night = false;
    const target = new THREE.Vector3(JOBS[0].to[0] * 7, 0, JOBS[0].to[1] * 7);
    marker.position.set(target.x, 1.2, target.z);
    ring.position.set(target.x, 0.06, target.z);

    const bumpCargo = (n: number) => {
      cargo = Math.max(0, cargo - n);
      arcadeAudio.blip(120, 0.08, 'sawtooth', 0.08);
    };

    let last = performance.now();
    let raf = 0;
    let dead = false;
    const finished = { current: false };

    const end = (won: boolean) => {
      if (finished.current) return;
      finished.current = true;
      onResult({
        score,
        won,
        stamps: { clear: won, skill: cargo >= 70 && won, mastery: won && cargo >= 90 },
      });
      setOver({ score, won });
    };

    const loop = (now: number) => {
      if (dead) return;
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      if (!finished.current) {
        const ax = inp.axis();
        yaw -= ax.x * 1.8 * dt;
        const acc = -ax.y * 18;
        vel.x += Math.sin(yaw) * acc * dt;
        vel.z += Math.cos(yaw) * acc * dt;
        vel.x *= 0.985;
        vel.z *= 0.985;
        const spd = Math.hypot(vel.x, vel.z);
        if (spd > 16) {
          vel.x *= 16 / spd;
          vel.z *= 16 / spd;
        }
        car.position.x += vel.x * dt;
        car.position.z += vel.z * dt;
        car.position.x = THREE.MathUtils.clamp(car.position.x, -34, 34);
        car.position.z = THREE.MathUtils.clamp(car.position.z, -34, 34);
        car.rotation.y = yaw;
        const drift = Math.abs(ax.x) * spd;
        if (drift > 8) score += dt * 4;

        for (const t of traffic) {
          if (t.dir === 0) {
            t.z += t.spd * dt;
            if (t.z > 34) t.z = -34;
            t.mesh.rotation.y = 0;
          } else {
            t.x += t.spd * dt;
            if (t.x > 34) t.x = -34;
            t.mesh.rotation.y = Math.PI / 2;
          }
          t.mesh.position.set(t.x, 0, t.z);
          if (t.mesh.position.distanceTo(car.position) < 1.8) {
            bumpCargo(8 * dt * 10);
            vel.x *= 0.4; vel.z *= 0.4;
          }
        }
        for (const b of buildings) {
          if (b.position.distanceTo(car.position) < 2.2) {
            bumpCargo(12 * dt * 8);
            vel.x *= 0.3; vel.z *= 0.3;
          }
        }

        time -= dt;
        if (car.position.distanceTo(target) < 2.2) {
          score += Math.floor(40 + time * 2 + cargo * 0.3);
          job += 1;
          arcadeAudio.blip(660, 0.1, 'triangle', 0.1);
          if (job >= JOBS.length) end(true);
          else {
            if (job === 5) {
              night = true;
              scene.background = new THREE.Color('#0d1730');
              scene.fog = new THREE.Fog('#0d1730', 18, 55);
              hemi.intensity = 0.35;
            }
            const j = JOBS[job];
            time = j.time;
            target.set(j.to[0] * 7, 0, j.to[1] * 7);
            marker.position.set(target.x, 1.2, target.z);
            ring.position.set(target.x, 0.06, target.z);
          }
        }
        if (time <= 0 || cargo <= 0) end(false);

        const camTarget = new THREE.Vector3(
          car.position.x - Math.sin(yaw) * 7,
          4.2,
          car.position.z - Math.cos(yaw) * 7,
        );
        camera.position.lerp(camTarget, 1 - Math.pow(0.001, dt));
        camera.lookAt(car.position.x, 0.8, car.position.z);
        marker.position.y = 1.2 + Math.sin(now / 200) * 0.15;
        ring.rotation.z = now / 700;

        setHud({
          job,
          time: Math.max(0, time),
          cargo: Math.floor(cargo),
          score: Math.floor(score),
          name: JOBS[Math.min(job, JOBS.length - 1)].name + (night ? ' · NIGHT' : ''),
        });
      }
      const w = el.clientWidth;
      const h = Math.max(320, el.clientHeight);
      if (renderer.domElement.width !== w || renderer.domElement.height !== h) {
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      inp.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      arcadeAudio.stopMusic();
    };
  }, [onResult]);

  return (
    <div className="game-root tidehaul" ref={host}>
      <div className="tide-hud">
        <span>{hud.name}</span>
        <span>{Math.ceil(hud.time)}s</span>
        <span>Cargo {hud.cargo}%</span>
        <span>{hud.score}</span>
        <span>Job {Math.min(hud.job + 1, 8)}/8</span>
      </div>
      <TouchPad input={input.current} />
      {over && (
        <div className="overlay">
          <div className="panel">
            <h2>{over.won ? 'Depot closed. Paid.' : 'The soup is soup.'}</h2>
            <p>Score {over.score.toLocaleString()}</p>
            <button type="button" className="play" onClick={() => location.reload()}>Again</button>
            <button type="button" className="ghost" onClick={onExit} style={{ marginTop: 8, width: '100%' }}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
